import { useEffect, useRef, useState } from 'react';

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import {
  useAcceptChatbotTemplateDefault,
  useChatbotTemplate,
  usePutChatbotTemplate,
  useResetChatbotTemplate,
} from '../../hooks';
import { templateLabelKeys } from '../../model/templateLabels';
import { inline422Message } from '../../model/templateErrors';
import type {
  ChatbotChannel,
  ChatbotTemplate,
  ChatbotTemplateAction,
  ChatbotTemplateCatalog,
  ChatbotTemplateCatalogItem,
  ChatbotTemplatePartFormat,
} from '../../model/types';
import { ActionsEditor } from './ActionsEditor';
import { PlaceholderChips } from './PlaceholderChips';
import { TemplatePreview } from './TemplatePreview';

export interface TemplateEditorDrawerProps {
  open: boolean;
  onClose: () => void;
  item: ChatbotTemplateCatalogItem;
  catalog: ChatbotTemplateCatalog;
  locale: string;
  canPublish: boolean;
  /** Seeds the General draft with this text on open (the "Copy to {{label}}" flow of an unlisted
   * row, templates doc §13.7) — nothing is written until Save. */
  seedText?: string;
}

type Scope = 'GENERAL' | ChatbotChannel;
const SCOPES: Scope[] = ['GENERAL', 'TELEGRAM', 'MAX'];

interface ScopeDraft {
  text: string;
  format: ChatbotTemplatePartFormat;
  actions: ChatbotTemplateAction[];
}

function textOf(
  data: ChatbotTemplate | undefined,
  from: 'parts' | 'effectiveParts' | 'defaultParts' = 'parts',
): string {
  return data?.[from]?.find((p) => p.type === 'TEXT')?.text ?? '';
}
function formatOf(
  data: ChatbotTemplate | undefined,
  from: 'parts' | 'effectiveParts' | 'defaultParts' = 'parts',
): ChatbotTemplatePartFormat {
  return data?.[from]?.find((p) => p.type === 'TEXT')?.format ?? 'PLAIN';
}
function substitute(text: string, samples: Record<string, string>): string {
  return text.replace(/\{\{(\w+)\}\}/g, (match, name: string) => samples[name] ?? match);
}
function seedFor(data: ChatbotTemplate | undefined): ScopeDraft {
  if (data?.customized)
    return { text: textOf(data, 'parts'), format: formatOf(data, 'parts'), actions: data.actions };
  if (data?.acceptedDefault) {
    return {
      text: textOf(data, 'effectiveParts'),
      format: formatOf(data, 'effectiveParts'),
      actions: data.effectiveActions,
    };
  }
  return {
    text: textOf(data, 'defaultParts'),
    format: formatOf(data, 'defaultParts'),
    actions: data?.defaultActions ?? [],
  };
}

/**
 * General | Telegram | MAX scoped editor for one catalogue item (templates doc §13.6). The catalogue
 * decides everything the static registry used to hard-code: placeholders, action rules, requirement
 * captions, the bot's own buttons — and the drawer no longer sends `kind` on save.
 */
export function TemplateEditorDrawer({
  open,
  onClose,
  item,
  catalog,
  locale,
  canPublish,
  seedText,
}: TemplateEditorDrawerProps) {
  const { t } = useTranslation('chatbot');
  const [scope, setScope] = useState<Scope>('GENERAL');
  const [drafts, setDrafts] = useState<Partial<Record<Scope, ScopeDraft>>>({});
  const textFieldRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) {
      setScope('GENERAL');
      setDrafts(
        seedText !== undefined ? { GENERAL: { text: seedText, format: 'PLAIN', actions: [] } } : {},
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item.contentKey, locale]);

  const channel = scope === 'GENERAL' ? undefined : scope;
  const query = useChatbotTemplate(
    item.contentKey,
    { locale, channel },
    open && (scope !== 'GENERAL' || seedText === undefined),
  );
  const generalData = scope === 'GENERAL' && seedText === undefined ? item.template : query.data;
  const putMutation = usePutChatbotTemplate();
  const resetMutation = useResetChatbotTemplate();
  const acceptDefaultMutation = useAcceptChatbotTemplateDefault();

  useEffect(() => {
    if (!generalData) return;
    if (scope === 'GENERAL' || generalData.customized) {
      setDrafts((current) =>
        current[scope] ? current : { ...current, [scope]: seedFor(generalData) },
      );
    }
  }, [generalData, scope]);

  const draft = drafts[scope];
  const { labelKey } = templateLabelKeys(item.contentKey);
  const label = t(labelKey, item.contentKey);

  const placeholderDefs = item.allowedPlaceholders
    .map((name) => catalog.placeholders.find((p) => p.name === name))
    .filter((p): p is (typeof catalog.placeholders)[number] => Boolean(p));
  const samples: Record<string, string> = Object.fromEntries(
    item.allowedPlaceholders.map((name) => [name, t(`templates.preview.samples.${name}`, name)]),
  );

  const setDraft = (patch: Partial<ScopeDraft>) =>
    setDrafts((current) => ({
      ...current,
      [scope]: { ...(current[scope] as ScopeDraft), ...patch },
    }));

  /** Placeholder chips insert at the caret, not appended at the end (templates doc §13.6). */
  const insertPlaceholderAtCaret = (currentText: string, token: string) => {
    const input = textFieldRef.current;
    const start = input?.selectionStart ?? currentText.length;
    const end = input?.selectionEnd ?? currentText.length;
    setDraft({ text: currentText.slice(0, start) + token + currentText.slice(end) });
    const caret = start + token.length;
    requestAnimationFrame(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    });
  };

  const startCustomise = () => {
    setDrafts((current) => ({ ...current, [scope]: seedFor(item.template) }));
  };

  function findUnknownPlaceholder(text: string): string | undefined {
    const matches = text.match(/\{\{(\w+)\}\}/g);
    return matches
      ?.map((m) => m.slice(2, -2))
      .find((name) => !item.allowedPlaceholders.includes(name));
  }
  const unknownInText = draft ? findUnknownPlaceholder(draft.text) : undefined;
  const unknownInActions = draft?.actions.map((a) => findUnknownPlaceholder(a.label)).find(Boolean);
  const unknownPlaceholder = unknownInText ?? unknownInActions;

  const scopeIssues = item.issues.filter((issue) =>
    scope === 'GENERAL' ? !issue.channel : issue.channel === scope,
  );

  const dirty = Boolean(
    draft &&
    (draft.text !== textOf(generalData, 'parts') ||
      JSON.stringify(draft.actions) !== JSON.stringify(generalData?.actions ?? []) ||
      seedText !== undefined),
  );

  const [saveError, setSaveError] = useState<{
    code: string;
    details?: Record<string, unknown>;
  } | null>(null);

  const handleSave = () => {
    if (!draft) return;
    setSaveError(null);
    putMutation.mutate(
      {
        key: item.contentKey,
        scope: { locale, channel },
        body: {
          parts: [{ type: 'TEXT', text: draft.text, format: draft.format }],
          actions: draft.actions,
          version: generalData?.version ?? null,
        },
      },
      {
        onSuccess: () => void query.refetch(),
        onError: (error) => {
          if (error.status === 422 && error.code) {
            setSaveError({
              code: error.code,
              details: error.details as Record<string, unknown> | undefined,
            });
          }
        },
      },
    );
  };

  const handleResetToGeneral = () => {
    if (channel === undefined) return;
    resetMutation.mutate(
      { key: item.contentKey, scope: { locale, channel } },
      {
        onSuccess: () => {
          setDrafts((current) => {
            const next = { ...current };
            delete next[scope];
            return next;
          });
          void query.refetch();
        },
      },
    );
  };

  const handleUsePlatformDefault = () => {
    acceptDefaultMutation.mutate(item.contentKey);
  };

  const isUnlisted = item.family === 'UNLISTED';
  const canSave =
    canPublish && !isUnlisted && Boolean(draft) && draft!.text.trim() !== '' && !unknownPlaceholder;

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100vw', md: 600 } } } }}
    >
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" noWrap>
                {label}
              </Typography>
              {item.requirement !== 'OPTIONAL' ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t(
                    item.requirement === 'ALWAYS'
                      ? 'templates.requirement.ALWAYS'
                      : 'templates.requirement.WHEN_AI_ON',
                  )}
                />
              ) : null}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {item.contentKey}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label={t('editor.close')}>
            <CloseRoundedIcon />
          </IconButton>
        </Stack>

        {item.renderMode === 'PER_ITEM' ? (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            {t('editor.renderMode.PER_ITEM')}
          </Typography>
        ) : item.renderMode === 'EMBEDDED' ? (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            {t('editor.renderMode.EMBEDDED')}
          </Typography>
        ) : null}
        {item.renderedTogetherWith.length > 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
            {t('editor.renderedTogether', {
              keys: item.renderedTogetherWith
                .map((key) => t(templateLabelKeys(key).labelKey, key))
                .join(', '),
            })}
          </Typography>
        ) : null}

        {isUnlisted ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            {t('templates.unlisted.readOnly')}
          </Alert>
        ) : null}

        {scopeIssues.length > 0 ? (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="subtitle2">{t('editor.issuesTitle')}</Typography>
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {scopeIssues.map((issue, index) => (
                <li key={index}>
                  <Typography variant="body2">
                    {t(
                      `templates.issues.${issue.code}`,
                      issue.detail ? { [issueDetailKey(issue.code)]: issue.detail } : undefined,
                    )}
                  </Typography>
                </li>
              ))}
            </Stack>
          </Alert>
        ) : null}

        {!isUnlisted ? (
          <Tabs value={scope} onChange={(_event, value: Scope) => setScope(value)} sx={{ mb: 2 }}>
            {SCOPES.map((s) => (
              <Tab
                key={s}
                value={s}
                label={
                  s !== 'GENERAL' && item.channelVariants.includes(s) ? (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <span>{t(`editor.scope.${s}`)}</span>
                      <Box
                        sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }}
                      />
                    </Stack>
                  ) : (
                    t(`editor.scope.${s}`)
                  )
                }
              />
            ))}
          </Tabs>
        ) : null}

        <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
          {query.isLoading && scope !== 'GENERAL' ? (
            <LoadingState label={t('editor.loading')} fullHeight={false} />
          ) : query.isError && scope !== 'GENERAL' ? (
            <ErrorState
              title={t('editor.loadErrorTitle')}
              message={query.error?.message}
              onRetry={() => void query.refetch()}
            />
          ) : !draft ? (
            <Stack spacing={2}>
              <EmptyState
                title={t('editor.usesGeneralTitle')}
                description={t('editor.usesGeneralDescription')}
              />
              <TemplatePreview
                text={textOf(item.template, 'effectiveParts') || null}
                actions={item.template.effectiveActions}
                caption={t('editor.previewCaption.general')}
              />
              {canPublish ? (
                <Button
                  variant="contained"
                  onClick={startCustomise}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  {t('editor.customiseFor', { channel: t(`editor.scope.${scope}`) })}
                </Button>
              ) : null}
            </Stack>
          ) : (
            <Stack spacing={2}>
              {saveError ? <Alert severity="error">{inline422Message(saveError, t)}</Alert> : null}
              <TextField
                label={t('editor.textLabel')}
                value={draft.text}
                onChange={(event) => setDraft({ text: event.target.value })}
                inputRef={textFieldRef}
                multiline
                minRows={4}
                fullWidth
                disabled={!canPublish || isUnlisted}
                error={Boolean(unknownPlaceholder) || draft.text.length > 4096}
                helperText={
                  unknownPlaceholder
                    ? t('editor.errors.placeholders', {
                        placeholders: `{{${unknownPlaceholder}}}`,
                        allowed: item.allowedPlaceholders.join(', '),
                      })
                    : `${draft.text.length} / 4096`
                }
                slotProps={{ htmlInput: { 'aria-label': t('editor.textLabel') } }}
              />
              <ToggleButtonGroup
                exclusive
                size="small"
                value={draft.format}
                disabled={!canPublish || isUnlisted}
                onChange={(_event, value: ChatbotTemplatePartFormat | null) =>
                  value && setDraft({ format: value })
                }
              >
                <ToggleButton value="PLAIN">{t('editor.formatOptions.plain')}</ToggleButton>
                <ToggleButton value="MARKDOWN">{t('editor.formatOptions.markdown')}</ToggleButton>
                <ToggleButton value="HTML">{t('editor.formatOptions.html')}</ToggleButton>
              </ToggleButtonGroup>

              <PlaceholderChips
                placeholders={placeholderDefs}
                onInsert={(placeholder) =>
                  insertPlaceholderAtCaret(draft.text, `{{${placeholder}}}`)
                }
                disabled={!canPublish || isUnlisted}
              />

              <ActionsEditor
                actions={draft.actions}
                onChange={(actions) => setDraft({ actions })}
                disabled={!canPublish || isUnlisted}
                actionMarkup={item.actionMarkup}
                deepLinkTargets={catalog.deepLinkTargets}
                hubActions={item.hubActions}
                expectedActionTypes={item.expectedActionTypes}
                contentLanguageLabel={locale}
              />

              <TemplatePreview
                text={substitute(draft.text, samples) || null}
                actions={[
                  ...draft.actions,
                  ...item.hubActions.map((a) => ({
                    type: a.type,
                    label: hubActionPreviewLabel(a, t),
                    value: a.value,
                  })),
                ]}
                caption={t(
                  scope === 'GENERAL'
                    ? 'editor.previewCaption.general'
                    : 'editor.previewCaption.channel',
                  { channel: t(`editor.scope.${scope}`) },
                )}
              />

              {item.template.hasDefault ? (
                <Alert severity="info" variant="outlined">
                  {t('editor.platformDefaultNote')}
                  {canPublish && !item.template.acceptedDefault && !isUnlisted ? (
                    <Button size="small" onClick={handleUsePlatformDefault} sx={{ ml: 1 }}>
                      {t('templates.showFilterOptions.default')}
                    </Button>
                  ) : null}
                </Alert>
              ) : null}
            </Stack>
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}
        >
          {generalData?.version != null ? (
            <Chip
              size="small"
              variant="outlined"
              label={t('editor.versionChip', { version: generalData.version })}
            />
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          {scope !== 'GENERAL' && generalData?.customized && canPublish ? (
            <Button color="error" onClick={handleResetToGeneral} disabled={resetMutation.isPending}>
              {t('editor.resetToGeneral')}
            </Button>
          ) : null}
          <Button onClick={onClose}>{t('editor.cancel')}</Button>
          {draft && !isUnlisted ? (
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!canSave || !dirty || putMutation.isPending}
              startIcon={
                putMutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined
              }
            >
              {putMutation.isPending ? t('editor.saving') : t('editor.save')}
            </Button>
          ) : null}
        </Stack>
      </Box>
    </Drawer>
  );
}

function issueDetailKey(code: string): string {
  switch (code) {
    case 'PLACEHOLDER_NOT_SUPPLIED':
      return 'placeholder';
    case 'DEEP_LINK_TARGET_UNKNOWN':
      return 'target';
    case 'COMPOSED_MARKUP_CONFLICT':
      return 'other';
    case 'EXPECTED_ACTION_MISSING':
      return 'action';
    case 'KEY_RETIRED':
      return 'replacement';
    case 'USED_AS_FAQ_ANSWER':
      return 'intent';
    default:
      return 'detail';
  }
}

function hubActionPreviewLabel(
  action: { type: string; value: string },
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  if (action.type === 'DEEP_LINK' && action.value === 'catalog')
    return t('editor.actions.hub.catalog');
  if (action.type === 'DEEP_LINK') return t('editor.actions.hub.order');
  if (action.type === 'QUICK_REPLY') return t('editor.actions.hub.commandMenu');
  return t('editor.actions.hub.language');
}
